import { makeAutoObservable } from 'mobx';
import { JobSummaryOutput } from 'dtos/jobs/JobSummaryOutput';
import { TabType } from 'types/jobs';
import { addressFormatter } from 'utils/formatter';

const ITEMS_PER_PAGE: number = 20;

type StrippedJobSummaryOutput = { job: JobSummaryOutput; strippedAddress: string };

export class JobsFilterStore {
  jobs: JobSummaryOutput[];
  searchQuery: string;
  page: number;

  constructor(jobs: JobSummaryOutput[]) {
    this.jobs = jobs;
    this.searchQuery = '';
    this.page = 1;
    makeAutoObservable(this, {}, { autoBind: true });
  }

  search(query: string) {
    this.searchQuery = query;
  }

  setPage(pageIndex: number) {
    this.page = pageIndex;
  }

  pagesCount(forTab: TabType): number {
    switch (forTab) {
      case TabType.ActiveReports:
        return Math.ceil(this.currentReportsNumber / ITEMS_PER_PAGE);
      case TabType.CompletedReports:
        return Math.ceil(this.completedReportsNumber / ITEMS_PER_PAGE);
      case TabType.AllReports:
        return Math.ceil(this.allReportsNumber / ITEMS_PER_PAGE);
    }
  }

  get allReportsNumber(): number {
    return this.jobs.length;
  }

  get allReports(): JobSummaryOutput[] {
    return this.jobs.slice((this.page - 1) * ITEMS_PER_PAGE, this.page * ITEMS_PER_PAGE);
  }

  get currentReportsNumber(): number {
    return this.jobs.filter((job) => !this.isCompleted(job)).length;
  }

  get currentReports(): JobSummaryOutput[] {
    return this.jobs
      .filter((job) => !this.isCompleted(job))
      .slice((this.page - 1) * ITEMS_PER_PAGE, this.page * ITEMS_PER_PAGE);
  }

  get completedReportsNumber(): number {
    return this.jobs.filter((job) => this.isCompleted(job)).length;
  }

  get completedReports(): JobSummaryOutput[] {
    return this.jobs
      .filter((job) => this.isCompleted(job))
      .slice((this.page - 1) * ITEMS_PER_PAGE, this.page * ITEMS_PER_PAGE);
  }

  get matchedReports(): JobSummaryOutput[] {
    const strippedSearchQuery = this.stripForSearch(this.searchQuery);

    if (strippedSearchQuery === undefined || strippedSearchQuery.length === 0) {
      return this.currentReports;
    }

    const matchingReports = this.strippedAddresses
      .filter((strippedJob) => {
        return strippedJob.strippedAddress.includes(strippedSearchQuery);
      })
      .map((strippedJob) => strippedJob.job);

    return matchingReports;
  }

  get isSearching(): boolean {
    return this.searchQuery !== '';
  }

  private get strippedAddresses(): StrippedJobSummaryOutput[] {
    return this.jobs.map((job) => {
      const strippedAddress = this.stripForSearch(addressFormatter(job.address));
      return { job, strippedAddress };
    });
  }

  private isCompleted(job: JobSummaryOutput): boolean {
    return job.reportStatus === 'Complete' || job.reportStatus === 'Canceled' || job.reportStatus === 'Draft Submitted';
  }

  private stripForSearch(address: string): string {
    return address.toLowerCase().replace(/[^a-z0-9]/g, '');
  }
}
